import React, { useState } from 'react';
import { CheckCircle, ChevronDown, XCircle } from 'lucide-react';
import CatalogProductImage from './CatalogProductImage';

/**
 * Small receipt kept in chat after the full Product Analysis screen closes.
 * The structured payload remains attached to the message so follow-up chat can
 * use it, while the default view stays compact on a phone.
 */
const ProductAnalysisSummaryCard = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  if (!data || data.type !== 'product_followup') return null;

  const product = data.product || {};
  const analysis = data.analysis || {};
  const pros = analysis.pros || analysis.whatsGreat || [];
  const cons = analysis.cons || analysis.concerns || [];
  const score = data.matchScore ?? 0;
  const isFormulaOnly = data.personalizationBasis === 'formula_only';
  const keyIngredients = (
    product.keyIngredients?.length ? product.keyIngredients
      : analysis.keyIngredients?.length ? analysis.keyIngredients
        : analysis.key_ingredients?.length ? analysis.key_ingredients
          : typeof analysis.ingredients_highlight === 'string'
            ? analysis.ingredients_highlight.split(/(?<!\d)[,;](?!\d)\s*/).map((item) => item.trim()).filter(Boolean)
            : []
  ).slice(0, 6);

  const isError = /^(error|unable to read|unknown product)$/i.test((product.name || '').trim())
    || /^error$/i.test((product.brand || '').trim())
    || Boolean(analysis.error);

  if (isError) {
    return (
      <div className="max-w-md rounded-2xl border border-[#341C1C]/10 bg-white p-4 text-left shadow-sm">
        <p className="text-sm font-semibold text-[#2A324B]">Couldn’t read this product clearly</p>
        <p className="mt-1 text-[13px] leading-relaxed text-[#2A324B]/60">Try a sharper label photo, or type the product name.</p>
      </div>
    );
  }

  return (
    <div className="max-w-md overflow-hidden rounded-2xl border border-[#341C1C]/10 bg-white text-left shadow-sm">
      <div className="flex items-center gap-3 p-3.5">
        <div className="h-12 w-12 flex-none overflow-hidden rounded-xl border border-[#341C1C]/8">
          <CatalogProductImage src={product.image} alt={product.name || 'Product'} brand={product.brand || ''} className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          {product.brand && <p className="truncate text-[12px] font-semibold uppercase tracking-[0.12em] text-[#2A324B]/45">{product.brand}</p>}
          <p className="text-[15px] font-semibold leading-snug text-[#2A324B]">{product.name || 'Product analysis'}</p>
          <p className="mt-1 text-[12px] text-[#2A324B]/55">{isFormulaOnly ? 'General formula assessment' : 'Personalised product assessment'}</p>
        </div>
        <div className="flex-none rounded-full bg-[#F5EBDD] px-3 py-1.5 text-center text-[#2A324B]">
          <span className="block text-[15px] font-bold leading-none">{score}</span>
          <span className="mt-0.5 block text-[12px] font-semibold uppercase tracking-wide">{isFormulaOnly ? 'formula' : 'match'}</span>
        </div>
      </div>

      {(keyIngredients.length > 0 || pros.length > 0 || cons.length > 0) && (
        <>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded((value) => !value)}
            className="flex w-full items-center justify-between border-t border-[#341C1C]/8 px-3.5 py-2.5 text-left text-[13px] font-semibold text-[#2A324B]"
          >
            <span>{expanded ? 'Hide summary' : 'View summary'}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
          {expanded && (
            <div className="space-y-2.5 border-t border-[#341C1C]/8 px-3.5 py-3">
              {keyIngredients.length > 0 && (
                <p className="text-[13px] leading-relaxed text-[#2A324B]/75"><strong>Key ingredients:</strong> {keyIngredients.join(', ')}</p>
              )}
              {pros[0] && (
                <div className="flex items-start gap-2 text-[13px] leading-relaxed text-[#2A324B]/80"><CheckCircle className="mt-0.5 h-4 w-4 flex-none text-emerald-600" aria-hidden="true" /><span>{pros[0]}</span></div>
              )}
              {cons[0] && (
                <div className="flex items-start gap-2 text-[13px] leading-relaxed text-[#2A324B]/80"><XCircle className="mt-0.5 h-4 w-4 flex-none text-amber-600" aria-hidden="true" /><span>{cons[0]}</span></div>
              )}
              <p className="text-[12px] text-[#2A324B]/45">{isFormulaOnly ? 'Formula score only. Ask below to personalise it.' : 'Ask below to continue with this product in context.'}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductAnalysisSummaryCard;
